import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'
import supabase from '../../lib/supabase'

const {
  FiEdit, FiSave, FiX, FiPlus, FiTrash2, FiEye, FiEyeOff,
  FiSettings, FiRefreshCw, FiCheck, FiAlertCircle, FiCode,
  FiTrendingUp, FiShoppingCart, FiBookOpen, FiGlobe, FiSmartphone,
  FiArrowRight, FiStar, FiUsers, FiTarget, FiPlay, FiCalendar,
  FiSend, FiLogIn, FiBarChart2, FiActivity, FiChevronDown
} = FiIcons

// Available icons for buttons
const availableIcons = {
  'FiArrowRight': FiArrowRight,
  'FiPlay': FiPlay,
  'FiCode': FiCode,
  'FiTrendingUp': FiTrendingUp,
  'FiShoppingCart': FiShoppingCart,
  'FiBookOpen': FiBookOpen,
  'FiGlobe': FiGlobe,
  'FiSmartphone': FiSmartphone,
  'FiStar': FiStar,
  'FiUsers': FiUsers,
  'FiTarget': FiTarget,
  'FiCalendar': FiCalendar,
  'FiSend': FiSend,
  'FiLogIn': FiLogIn,
  'FiBarChart2': FiBarChart2,
  'FiActivity': FiActivity,
  'FiCheck': FiCheck,
  'FiSettings': FiSettings,
  'FiEye': FiEye
}

// Simple Select Component
const SimpleSelect = ({ options, value, onChange, placeholder, className = '' }) => {
  const [isOpen, setIsOpen] = useState(false)
  const selectedOption = options.find(opt => opt.value === value)

  return (
    <div className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full p-2 border border-gray-300 rounded-lg bg-white text-left flex items-center justify-between focus:ring-2 focus:ring-blue-500 focus:border-transparent"
      >
        <span className="flex items-center">
          {selectedOption?.icon && (
            <SafeIcon icon={selectedOption.icon} className="mr-2 h-4 w-4" />
          )}
          {selectedOption?.label || placeholder}
        </span>
        <SafeIcon 
          icon={FiChevronDown} 
          className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} 
        />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto"
          >
            {options.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => {
                  onChange(option)
                  setIsOpen(false)
                }}
                className="w-full p-2 text-left hover:bg-gray-100 flex items-center"
              >
                {option.icon && (
                  <SafeIcon icon={option.icon} className="mr-2 h-4 w-4" />
                )}
                {option.label}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

// Select options
const typeOptions = [
  { value: 'button', label: 'Button' },
  { value: 'submit', label: 'Submit' },
  { value: 'reset', label: 'Reset' }
]

const variantOptions = [
  { value: 'primary', label: 'Primary' },
  { value: 'secondary', label: 'Secondary' },
  { value: 'outline', label: 'Outline' },
  { value: 'ghost', label: 'Ghost' },
  { value: 'danger', label: 'Danger' }
]

const sizeOptions = [
  { value: 'sm', label: 'Small' },
  { value: 'md', label: 'Medium' },
  { value: 'lg', label: 'Large' },
  { value: 'xl', label: 'Extra Large' }
]

const effectOptions = [
  { value: 'none', label: 'None' },
  { value: 'scale', label: 'Scale' },
  { value: 'spin', label: 'Spin' },
  { value: 'fade', label: 'Fade' },
  { value: 'bounce', label: 'Bounce' },
  { value: 'slide', label: 'Slide' },
  { value: 'glow', label: 'Glow' }
]

const iconOptions = Object.keys(availableIcons).map(iconName => ({
  value: iconName,
  label: iconName,
  icon: availableIcons[iconName]
}))

const iconPositionOptions = [
  { value: 'none', label: 'No Icon' },
  { value: 'left', label: 'Left' },
  { value: 'right', label: 'Right' }
]

const linkTypeOptions = [
  { value: 'internal', label: 'Internal' },
  { value: 'external', label: 'External' },
  { value: 'scroll', label: 'Scroll to Section' },
  { value: 'modal', label: 'Open Modal' }
]

const ButtonsManager = () => {
  const [buttons, setButtons] = useState([])
  const [loading, setLoading] = useState(true)
  const [editingButton, setEditingButton] = useState(null)
  const [showAddForm, setShowAddForm] = useState(false)
  const [formData, setFormData] = useState({})
  const [message, setMessage] = useState({ type: '', text: '' })

  const defaultFormData = {
    button_key: '',
    text: '',
    type: 'button',
    variant: 'primary',
    size: 'md',
    icon: '',
    icon_position: 'left',
    base_classes: '',
    hover_classes: '',
    active_classes: '',
    disabled_classes: '',
    effect_type: 'none',
    effect_active: true,
    motion_props: '{}',
    link_url: '',
    link_type: 'internal',
    onclick_action: '',
    is_active: true,
    component_location: '',
    section_id: '',
    display_order: 0
  }

  useEffect(() => {
    fetchButtons()
  }, [])

  useEffect(() => {
    if (message.text) {
      const timer = setTimeout(() => {
        setMessage({ type: '', text: '' })
      }, 3000)
      return () => clearTimeout(timer)
    }
  }, [message])

  const fetchButtons = async () => {
    try {
      setLoading(true)
      const { data, error } = await supabase
        .from('codevaro_buttons')
        .select('*')
        .order('component_location', { ascending: true })
        .order('display_order', { ascending: true })

      if (error) throw error
      setButtons(data || [])
    } catch (error) {
      console.error('Error fetching buttons:', error)
      setMessage({ type: 'error', text: 'Σφάλμα κατά τη φόρτωση των κουμπιών' })
    } finally {
      setLoading(false)
    }
  }

  const handleEdit = (button) => {
    setEditingButton(button.id)
    setFormData({
      ...button,
      motion_props: JSON.stringify(button.motion_props || {}, null, 2)
    })
  }

  const handleSave = async () => {
    try {
      let motionProps = {}
      try {
        motionProps = formData.motion_props ? JSON.parse(formData.motion_props) : {}
      } catch (parseError) {
        setMessage({ type: 'error', text: 'Μη έγκυρο JSON format στο Motion Props' })
        return
      }
      
      const updateData = {
        ...formData,
        motion_props: motionProps,
        updated_at: new Date().toISOString()
      }

      const { error } = await supabase
        .from('codevaro_buttons')
        .update(updateData)
        .eq('id', editingButton)

      if (error) throw error

      setMessage({ type: 'success', text: 'Το κουμπί ενημερώθηκε επιτυχώς!' })
      setEditingButton(null)
      setFormData({})
      fetchButtons()
    } catch (error) {
      console.error('Error updating button:', error)
      setMessage({ type: 'error', text: 'Σφάλμα κατά την ενημέρωση του κουμπιού' })
    }
  }

  const handleAdd = async () => {
    try {
      if (!formData.button_key || !formData.text) {
        setMessage({ type: 'error', text: 'Συμπληρώστε τουλάχιστον το Button Key και το Text' })
        return
      }

      let motionProps = {}
      try {
        motionProps = formData.motion_props ? JSON.parse(formData.motion_props) : {}
      } catch (parseError) {
        setMessage({ type: 'error', text: 'Μη έγκυρο JSON format στο Motion Props' })
        return
      }
      
      const insertData = {
        ...formData,
        motion_props: motionProps
      }

      const { error } = await supabase
        .from('codevaro_buttons')
        .insert([insertData])

      if (error) throw error

      setMessage({ type: 'success', text: 'Το κουμπί προστέθηκε επιτυχώς!' })
      setShowAddForm(false)
      setFormData({})
      fetchButtons()
    } catch (error) {
      console.error('Error adding button:', error)
      setMessage({ type: 'error', text: 'Σφάλμα κατά την προσθήκη του κουμπιού' })
    }
  }

  const handleDelete = async (buttonId) => {
    if (!confirm('Είστε σίγουροι ότι θέλετε να διαγράψετε αυτό το κουμπί;')) return

    try {
      const { error } = await supabase
        .from('codevaro_buttons')
        .delete()
        .eq('id', buttonId)

      if (error) throw error

      setMessage({ type: 'success', text: 'Το κουμπί διαγράφηκε επιτυχώς!' })
      fetchButtons()
    } catch (error) {
      console.error('Error deleting button:', error)
      setMessage({ type: 'error', text: 'Σφάλμα κατά τη διαγραφή του κουμπιού' })
    }
  }

  const handleToggleActive = async (buttonId, currentStatus) => {
    try {
      const { error } = await supabase
        .from('codevaro_buttons')
        .update({ is_active: !currentStatus })
        .eq('id', buttonId)

      if (error) throw error

      setMessage({ 
        type: 'success', 
        text: `Το κουμπί ${!currentStatus ? 'ενεργοποιήθηκε' : 'απενεργοποιήθηκε'} επιτυχώς!` 
      })
      fetchButtons()
    } catch (error) {
      console.error('Error toggling button status:', error)
      setMessage({ type: 'error', text: 'Σφάλμα κατά την αλλαγή κατάστασης του κουμπιού' })
    }
  }

  const renderPreview = (button) => {
    const IconComponent = availableIcons[button.icon]
    const motionProps = button.motion_props || {}

    return (
      <motion.button
        {...motionProps}
        className={`${button.base_classes} ${button.hover_classes} preview-button text-xs px-3 py-2 max-w-40 truncate`}
        disabled={!button.is_active}
      >
        {button.icon_position === 'left' && IconComponent && (
          <SafeIcon icon={IconComponent} className="mr-1 h-3 w-3 inline" />
        )}
        {button.text}
        {button.icon_position === 'right' && IconComponent && (
          <SafeIcon icon={IconComponent} className="ml-1 h-3 w-3 inline" />
        )}
      </motion.button>
    )
  }

  const FormFields = ({ data, onChange, isEditing = false }) => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Button Key {!isEditing && <span className="text-red-500">*</span>}
        </label>
        <input
          type="text"
          value={data.button_key || ''}
          onChange={(e) => onChange({ ...data, button_key: e.target.value })}
          className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          disabled={isEditing}
          placeholder="unique_button_key"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Text <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={data.text || ''}
          onChange={(e) => onChange({ ...data, text: e.target.value })}
          className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="Button Text"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
        <SimpleSelect
          options={typeOptions}
          value={data.type}
          onChange={(option) => onChange({ ...data, type: option.value })}
          placeholder="Select type"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Variant</label>
        <SimpleSelect
          options={variantOptions}
          value={data.variant}
          onChange={(option) => onChange({ ...data, variant: option.value })}
          placeholder="Select variant"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Size</label>
        <SimpleSelect
          options={sizeOptions}
          value={data.size}
          onChange={(option) => onChange({ ...data, size: option.value })}
          placeholder="Select size"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Icon</label>
        <SimpleSelect
          options={[{ value: '', label: 'No Icon' }, ...iconOptions]}
          value={data.icon}
          onChange={(option) => onChange({ ...data, icon: option.value })}
          placeholder="Select icon"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Icon Position</label>
        <SimpleSelect
          options={iconPositionOptions}
          value={data.icon_position}
          onChange={(option) => onChange({ ...data, icon_position: option.value })}
          placeholder="Select position"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Effect Type</label>
        <SimpleSelect
          options={effectOptions}
          value={data.effect_type}
          onChange={(option) => onChange({ ...data, effect_type: option.value })}
          placeholder="Select effect"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Link Type</label>
        <SimpleSelect
          options={linkTypeOptions}
          value={data.link_type}
          onChange={(option) => onChange({ ...data, link_type: option.value })}
          placeholder="Select link type"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Component Location</label>
        <input
          type="text"
          value={data.component_location || ''}
          onChange={(e) => onChange({ ...data, component_location: e.target.value })}
          className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="HeroSection"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Section ID</label>
        <input
          type="text"
          value={data.section_id || ''}
          onChange={(e) => onChange({ ...data, section_id: e.target.value })}
          className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="hero"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Display Order</label>
        <input
          type="number"
          value={data.display_order || 0}
          onChange={(e) => onChange({ ...data, display_order: parseInt(e.target.value) || 0 })}
          className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      <div className="md:col-span-2">
        <label className="block text-sm font-medium text-gray-700 mb-1">Base Classes</label>
        <textarea
          value={data.base_classes || ''}
          onChange={(e) => onChange({ ...data, base_classes: e.target.value })}
          className="w-full p-2 border border-gray-300 rounded-lg h-20 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium"
        />
      </div>

      <div className="md:col-span-2">
        <label className="block text-sm font-medium text-gray-700 mb-1">Hover Classes</label>
        <textarea
          value={data.hover_classes || ''}
          onChange={(e) => onChange({ ...data, hover_classes: e.target.value })}
          className="w-full p-2 border border-gray-300 rounded-lg h-20 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="hover:bg-blue-700 hover:shadow-lg transition-all duration-300"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Link URL</label>
        <input
          type="text"
          value={data.link_url || ''}
          onChange={(e) => onChange({ ...data, link_url: e.target.value })}
          className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="#contact or https://example.com"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">OnClick Action</label>
        <input
          type="text"
          value={data.onclick_action || ''}
          onChange={(e) => onChange({ ...data, onclick_action: e.target.value })}
          className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="handleClick"
        />
      </div>

      <div className="md:col-span-2">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Motion Props (JSON)
          <span className="text-xs text-gray-500 ml-2">Framer Motion animation properties</span>
        </label>
        <textarea
          value={data.motion_props || '{}'}
          onChange={(e) => onChange({ ...data, motion_props: e.target.value })}
          className="w-full p-2 border border-gray-300 rounded-lg h-24 font-mono text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder='{"whileHover": {"scale": 1.05}, "whileTap": {"scale": 0.95}}'
        />
      </div>

      <div className="flex items-center space-x-4">
        <label className="flex items-center">
          <input
            type="checkbox"
            checked={data.effect_active || false}
            onChange={(e) => onChange({ ...data, effect_active: e.target.checked })}
            className="mr-2"
          />
          Effect Active
        </label>

        <label className="flex items-center">
          <input
            type="checkbox"
            checked={data.is_active || false}
            onChange={(e) => onChange({ ...data, is_active: e.target.checked })}
            className="mr-2"
          />
          Button Active
        </label>
      </div>
    </div>
  )

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <SafeIcon icon={FiRefreshCw} className="animate-spin h-8 w-8 text-blue-500" />
        <span className="ml-3 text-gray-600">Φόρτωση κουμπιών...</span>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Διαχείριση Κουμπιών</h2>
          <p className="text-gray-600">Διαχειριστείτε όλα τα κουμπιά της ιστοσελίδας</p>
        </div>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => {
            setShowAddForm(true)
            setFormData(defaultFormData)
          }}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium inline-flex items-center transition-colors"
        >
          <SafeIcon icon={FiPlus} className="mr-2" />
          Προσθήκη Κουμπιού
        </motion.button>
      </div>

      {/* Message */}
      <AnimatePresence>
        {message.text && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className={`p-4 rounded-lg ${
              message.type === 'success' 
                ? 'bg-green-100 text-green-700 border border-green-200' 
                : 'bg-red-100 text-red-700 border border-red-200'
            }`}
          >
            <div className="flex items-center">
              <SafeIcon 
                icon={message.type === 'success' ? FiCheck : FiAlertCircle} 
                className="mr-2" 
              />
              {message.text}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add Form Modal */}
      <AnimatePresence>
        {showAddForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
            onClick={() => setShowAddForm(false)}
          >
            <motion.div
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.5, opacity: 0 }}
              className="bg-white rounded-xl p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold text-gray-900">Προσθήκη Νέου Κουμπιού</h3>
                <button
                  onClick={() => setShowAddForm(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <SafeIcon icon={FiX} className="h-6 w-6" />
                </button>
              </div>

              <FormFields data={formData} onChange={setFormData} />

              <div className="flex justify-end space-x-4 mt-6 pt-6 border-t border-gray-200">
                <button
                  onClick={() => setShowAddForm(false)}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Ακύρωση
                </button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleAdd}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium inline-flex items-center transition-colors"
                >
                  <SafeIcon icon={FiSave} className="mr-2" />
                  Αποθήκευση
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Buttons Table */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Κουμπί
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Τοποθεσία
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Προεπισκόπηση
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Κατάσταση
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ενέργειες
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {buttons.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                    <SafeIcon icon={FiEye} className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                    <p className="text-lg">Δεν υπάρχουν κουμπιά</p>
                    <p className="text-sm">Προσθέστε το πρώτο κουμπί για να ξεκινήσετε</p>
                  </td>
                </tr>
              ) : (
                buttons.map((button) => (
                  <tr key={button.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {button.text}
                        </div>
                        <div className="text-xs text-gray-500">
                          {button.button_key}
                        </div>
                        <div className="text-xs text-gray-400">
                          {button.type} • {button.variant} • {button.size}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{button.component_location}</div>
                      <div className="text-xs text-gray-500">{button.section_id}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {renderPreview(button)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() => handleToggleActive(button.id, button.is_active)}
                          className={`p-1 rounded-full ${
                            button.is_active 
                              ? 'text-green-600 hover:bg-green-100' 
                              : 'text-gray-400 hover:bg-gray-100'
                          }`}
                        >
                          <SafeIcon icon={button.is_active ? FiEye : FiEyeOff} className="h-4 w-4" />
                        </motion.button>
                        <span className={`text-xs ${
                          button.is_active ? 'text-green-600' : 'text-gray-400'
                        }`}>
                          {button.is_active ? 'Ενεργό' : 'Ανενεργό'}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() => handleEdit(button)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Επεξεργασία κουμπιού"
                        >
                          <SafeIcon icon={FiEdit} className="h-4 w-4" />
                        </motion.button>
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() => handleDelete(button.id)}
                          className="text-red-600 hover:text-red-900"
                          title="Διαγραφή κουμπιού"
                        >
                          <SafeIcon icon={FiTrash2} className="h-4 w-4" />
                        </motion.button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit Form Modal */}
      <AnimatePresence>
        {editingButton && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
            onClick={() => setEditingButton(null)}
          >
            <motion.div
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.5, opacity: 0 }}
              className="bg-white rounded-xl p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold text-gray-900">Επεξεργασία Κουμπιού</h3>
                <button
                  onClick={() => setEditingButton(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <SafeIcon icon={FiX} className="h-6 w-6" />
                </button>
              </div>

              <FormFields data={formData} onChange={setFormData} isEditing={true} />

              <div className="flex justify-end space-x-4 mt-6 pt-6 border-t border-gray-200">
                <button
                  onClick={() => setEditingButton(null)}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Ακύρωση
                </button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleSave}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium inline-flex items-center transition-colors"
                >
                  <SafeIcon icon={FiSave} className="mr-2" />
                  Αποθήκευση
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default ButtonsManager